import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import * as FullStory from '@fullstory/browser';
import ReactGA from 'react-ga';
import { removeOrgId } from './common/utils';

import { getClass } from '../../utils/component-helpers';

import analyticService from '../../services/analytics';
import { authService } from '../../services/auth';
import Constants from '../../app.constants';
import { session } from '../../services/session';

import NavHeader from './navigation/header/nav-header';
import NavSidebar from './navigation/sidebar/flagged-sidebar';
import Routes from '../../routes';
import Snackbar from '../misc/snackbar';

import './main.scss';
import '../../styles/constants.scss';
import '../../styles/narrativ_icons.scss';
import '../misc/tooltip.scss';

class Main extends Component {
  constructor(props) {
    super(props);

    this.AnalyticService = analyticService;
    this.AuthService = authService;
    this.Constants = Constants;
    this.Session = session;

    this.unauthorizedMap = {
      notLoggedIn: ['account', 'admin', 'merchant', 'product_match', 'publisher'],
      product_match: ['publisher', 'merchant'],
      merchant: ['admin', 'publisher', 'product_match'],
      publisher: ['admin', 'merchant', 'product_match'],
    };

    /*
     * Use Cases:
     * Sets a 'static-route' class to the page-container
     * Conditional for rendering NavHeader and NavSidebar components
     * Passed as a prop to NavHeader and NavSidebar components
     * Page renders are delayed until the Session is set unless it's a static route
     * Used in conjunction with noFooterRoutes to render a footer on static routes
     */
    this.staticRoutes = [
      '/v2/article-search',
      '/v2/change-password',
      '/v2/create-user',
      '/v2/login',
      '/v2/privacy-policy',
      '/v2/publisher/terms-conditions',
      '/v2/reset-password-completion',
      '/v2/reset-password-request',
    ];

    /*
     * Use Cases:
     * Used in conjunction with staticRoutes to render a footer on static routes
     * Conditional for ignoring moveContentRight when side nav is open
     */
    this.noFooterRoutes = [
      '/v2/article-search',
      '/v2/create-user',
      '/v2/login',
      '/v2/privacy-policy',
      '/v2/publisher/terms-conditions',
      '/v2/reset-password-completion',
      '/v2/reset-password-request',
    ];

    /*
     * Use Cases:
     * Pages that have been implemented in React, only available to admins
     * Passed as a prop to NavSidebar
     */
    this.restrictedPaths = [
      '/article-search/keyword',
      '/article-search/brand',
      '/article-search/product',
      '/new-recs/cart-explorer',
      '/accounts/widgets/jstags',
      '/accounts/overview',
      '/collections',
      '/campaign-center',
      '/smartlinks/edit',
      '/smartlinks/editor',
    ];

    this.productMatchPaths = ['/smartlinks/editor', '/smartlinks/edit'];

    this.wooshSpaRoutes = [
      '/product-match/smartlink-search',
      '/product-match/new-product',
      '/accounts/widgets',
    ];

    /*
     * Use Cases:
     * Conditional in isUnexposedRoute for exposed routes during navigation
     * Passed as a prop to NavSidebar
     */
    this.exposedPaths = [
      //  Admins
      '/bid-center/auction-config-editor',
      '/bid-center/bulk-match',
      '/bid-center/merchant-overview',
      '/bid-center/merch-ed-overview',
      '/bid-center/publisher-overview',
      '/creator-workspace/content-manager',

      // Publishers
      '/megatables/merchants',
      '/megatables/products',
      '/megatables/smartlinks',
      '/analytics',
      '/analytics/merchants',
      '/analytics/products',
      '/analytics/smartlinks',
      '/new-recs/product-research',
      '/new-recs/rates',
      '/new-recs/search-trends',
      '/link-creation',
      '/account-settings/general-settings',
      '/account-settings/payments',
      '/account-settings/payments/payouts',
      '/account-settings/payments/earnings',
      '/account-settings/payments/payment-setup',
      '/account-settings/developer-options',
      '/payments/earnings',
      '/payments/payment-setup',
      '/payments/payouts',
      '/orders',
      '/links/my-links',
      '/links/create-links/select-story',
      '/links/create-links/paste-product-urls',
      '/links/create-links/validate-links',
      '/links/create-links/copy-links',
      '/links/create-links/created-links',
      '/home-page',

      // Merchants
      '/desk',
      '/megatables/recommendations',
      '/megatables/campaign-performance',
      '/megatables/publishers',
      '/campaigns/new',
      '/campaigns/optimize',

      // Shared
      '/dashboard',
      '/megatables/articles',
      '/analytics/articles',
      '/terms-conditions',
      '/v2', // We reach this path when logging out of the dashboard
    ];

    /*
     * Use Cases:
     * Pages that don't require authentication
     */
    this.noAuthRoutes = [
      '/',
      '/v2/article-search',
      '/v2/change-password',
      '/v2/create-user',
      '/v2/login',
      '/v2/privacy-policy',
      '/v2/reset-password-completion',
      '/v2/reset-password-request',
    ];

    this.breakPoint = 1180;
    this.mobileBreakpoint = 1000;
    this.toggleNavSidebar = this.toggleNavSidebar.bind(this);

    this.loadedAnalytics = false;

    this.state = {
      currentUser: this.Session, // Empty session
      navSidebarCollapsed: false,
      isAdminView: null,
      isMobile: window.innerWidth <= this.mobileBreakpoint,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', () => this.checkWindowWidth());
    this.resumeFromLocalStorage();
    this.redirect();
    this.loadDatadog();

    this.trackGAPageView();

    this.nextWindowEvent = this.state.navSidebarCollapsed ? 'reveal' : 'hide';
    // Hide side nav if screen is narrow
    const merchEdOverview =
      this.props.location.pathname &&
      this.props.location.pathname.startsWith('/v2/admin/bid-center/merch-ed-overview');

    if (this.narrowScreen() || merchEdOverview) this.toggleNavSidebar('closed');
  }

  componentDidUpdate(prevProps) {
    /*
     * The path changes when login succeeds. We have to refresh
     * current user with the new session.
     */
    if (
      prevProps.location.pathname === '/v2/login' &&
      this.props.location.pathname !== '/v2/login'
    ) {
      this.resumeFromLocalStorage();
      this.redirect();
    }

    if (
      prevProps.location.pathname !== this.props.location.pathname &&
      this.props.location.pathname.startsWith('/v2/admin/bid-center/merch-ed-overview')
    ) {
      this.toggleNavSidebar('closed');
    }

    if (!session.isAdmin && !this.loadedAnalytics) {
      this.loadAnalytics();
    }

    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.trackGAPageView();
    }
  }

  loadAnalytics() {
    if (this.Constants.ENV === 'release' && session.userId !== 2464) {
      this.loadedAnalytics = true;

      FullStory.init({ orgId: '135TD1' });
      FullStory.event('Successful login', {});
      FullStory.identify(`${session.userId}`, {
        displayName: session.firstName,
        publisherType_int: session.publisher_type,
        isAdmin_bool: session.isAdmin,
        orgId_int: session.orgId,
        userRole_str: session.userRole,
      });
    }
  }

  trackGAPageView() {
    if (this.Constants.ENV === 'release') {
      ReactGA.set({ page: removeOrgId(this.props.location.pathname) });
      this.AnalyticService.trackEvent(
        removeOrgId(this.props.location.pathname),
        null,
        null,
        null,
        'pageview',
      );
    }
  }

  loadDatadog() {
    if (['staging', 'release'].indexOf(this.Constants.ENV) === -1) return;

    datadogRum.init({
      applicationId: 'bb27d471-49d3-4d90-ac74-9a275ce54ce3',
      clientToken: 'pub37e99a2ed619c30076747ad67ec260d0',
      site: 'datadoghq.com',
      service: 'narrativ-dash',
      env: this.Constants.ENV,
      version: this.Constants.BAMX_BUILD_VERSION,
      sampleRate: 100,
      trackInteractions: false, // don't turn on globally since it may expose sensitive data
    });
  }

  resumeFromLocalStorage() {
    // Load user session from localStorage
    const { currentUser } = window.localStorage;
    if (currentUser === undefined) return;

    try {
      this.Session.create(JSON.parse(currentUser));
    } catch (e) {
      console.error(e);
    }

    this.setState({
      currentUser: this.Session,
      isAdminView: this.Session.isAdminView,
    });

    this.AnalyticService.setUp(this.Constants.ENV === 'release', this.Session);

    if (this.Session.authVersion !== this.Constants.AUTH_VERSION) {
      this.AuthService.logout();
    }
  }

  /* User role and permissions */
  redirect() {
    /**
     * Based on the current URL path and the user role, determine if the user should
     * be able to access this route.
     *
     * If the user is authorized to view this route, return without redirecting.
     * If the user is unauthorized to view this route (i.e. a merchant attempting to view a publisher route),
     * redirect the user to their respective homepage.
     * If the user's session has expired, redirect the user to the login page.
     */
    const originalPath = this.props.location.pathname;
    const role = this.userRole();

    // Admin can access everything
    if (role === 'admin' || this.state.currentUser.isAdmin) return;

    if (this.isUnauthorized(role, originalPath)) {
      this.redirectUnauthorizedUser(role, originalPath);
    } else if (
      this.state.currentUser &&
      role === 'publisher' &&
      !this.state.currentUser.termsAccepted
    ) {
      // Redirect publishers who haven't accepted terms and conditions.
      this.redirectToTermsAndConditions();
    } else if (this.noAuthRoutes.includes(originalPath)) {
      // This is so we can test v2 login w/o infinite redirect to v1
    } else if (this.isUnexposedRoute(originalPath) && originalPath !== '/v2') {
      // If a non-admin tries to route to an unexposed v2 path, redirect them to their homepage
      this.props.history.push('/');
    }
  }

  redirectToTermsAndConditions() {
    /*
     * If the user is the admin of the account,
     * they'll be redirected to accept terms and conditions.
     * Secondary users are not allowed to access logged-in routes at all.
     */
    this.props.history.push('/v2/publisher/terms-conditions');
  }

  redirectUnauthorizedUser(role, originalPath) {
    if (role === 'notLoggedIn') {
      this.props.history.push({
        pathname: '/v2/login',
        search: `?redirect=${originalPath}`,
      });
    } else {
      /*
       * Logged in users trying to access routes
       * they are not allowed in get sent back to the homepage,
       * where they're redirected to the correct path by Redirect
       */
      this.props.history.push('/v2');
    }
  }

  userRole() {
    switch (this.state.currentUser.userRole) {
      case 'publishers':
        return 'publisher';
      case 'merchants':
        return 'merchant';
      case 'product_match':
        return 'product_match';
      case 'admin':
        return 'admin';
      default:
        return 'notLoggedIn';
    }
  }

  isAdmin() {
    const userRole = this.userRole();
    if (!userRole) return false;
    return userRole === 'admin' || userRole === 'product_match';
  }

  isUnauthorized(role, originalPath) {
    /*
     * Determine if users are attempting to access an unauthorized route
     *
     * Merchants and publishers trying to access routes outside their respective role are unauthorized
     * i.e. Publishers can't access merchant routes
     *
     * Product Match role can only access /v2/admin/product-match and /v2/admin/smartlinks
     */
    const orgRoute = originalPath.split('/')[2];
    const unauthorizedOrgRoute = this.unauthorizedMap[role].indexOf(orgRoute) > -1;

    const unauthorizedProductMatchRoute =
      role === 'product_match' &&
      !(
        originalPath.startsWith('/v2/admin/product-match') ||
        originalPath.startsWith('/v2/admin/smartlinks') ||
        originalPath === '/v2'
      );

    return unauthorizedOrgRoute || unauthorizedProductMatchRoute;
  }

  isUnexposedRoute(originalPath) {
    /*
     * Determine if the URL path is an unexposed route
     *
     * If the user is not an admin, the path is not a valid exposed org route,
     * and the path is not a valid exposed product match route
     */
    const routeMatches = originalPath.match(/(publisher|merchant)(\/\d+)*(\/.+)/);
    // Temp product match override to allow netscribe access to smartlink management page
    const productMatchRouteMatches = originalPath.match(/(admin)(\/smartlinks)(\/\d+)*(\/.+)/);
    const productMatchRoute =
      productMatchRouteMatches && productMatchRouteMatches[2] + productMatchRouteMatches[4];
    const productMatchOverride =
      this.state.currentUser.userRole === 'product_match' &&
      this.productMatchPaths.includes(productMatchRoute);

    return (
      !this.state.currentUser.isAdmin &&
      !window.localStorage.adminTests &&
      !this.exposedPaths.includes(routeMatches && routeMatches[3]) &&
      !productMatchOverride
    );
  }

  invalidNavRoute() {
    let invalidRoute = true;
    const paths = ['/v2/admin', '/v2/publisher', '/v2/merchant'];

    if (
      paths.some(substring => this.props.location.pathname.includes(substring)) &&
      this.props.location.pathname !== '/v2/publisher-faq' &&
      this.props.location.pathname !== '/v2/publisher/terms-conditions'
    ) {
      invalidRoute = false;
    }

    return invalidRoute;
  }

  /* Layout functions, organized alphabetically */
  checkWindowWidth() {
    if (this.narrowScreen() && this.nextWindowEvent === 'hide') {
      this.toggleNavSidebar('closed');
      this.nextWindowEvent = 'reveal';
    } else if (!this.narrowScreen() && this.nextWindowEvent === 'reveal') {
      this.toggleNavSidebar('open');
      this.nextWindowEvent = 'hide';
    }

    if (window.innerWidth <= this.mobileBreakpoint && !this.state.isMobile) {
      this.setState({ isMobile: true });
    } else if (window.innerWidth > this.mobileBreakpoint && this.state.isMobile) {
      this.setState({ isMobile: false });
    }
  }

  getContainerClass() {
    const path = this.props.location.pathname;
    return this.staticRoutes.includes(path) ? 'static-page' : '';
  }

  moveContentRight() {
    if (!this.state.currentUser || this.noFooterRoutes.includes(this.props.location.pathname))
      return false;

    return !this.state.navSidebarCollapsed && !this.invalidNavRoute();
  }

  narrowScreen() {
    return window.innerWidth < this.breakPoint;
  }

  toggleNavSidebar(state) {
    // state: open|closed
    const collapsed = state === 'closed' || !this.state.navSidebarCollapsed;
    this.setState({ navSidebarCollapsed: collapsed });
  }

  hideNav(currentPath) {
    // Hide the nav bars on certain pages
    const staticRoute = this.staticRoutes.some(path => currentPath.startsWith(path));
    const redirecting = ['/', '/v2'].includes(currentPath);
    const notLoggedIn = this.userRole() === 'notLoggedIn';

    return staticRoute || redirecting || notLoggedIn;
  }

  update() {
    /*
     * Keep navbar open and update navbar items when re-logging in as admin viewing as pub
     * except if returning to Overview v2 since the navbar be closed by default
     */
    if (
      !(
        this.props.location.pathname &&
        this.props.location.pathname.startsWith('/v2/admin/bid-center/merch-ed-overview')
      )
    ) {
      this.setState(prevState => prevState);
    }
  }

  render() {
    const currentPath = this.props.location.pathname;

    /*
     * Wait until Session becomes available before rendering
     * any logged-in component.
     */
    if (!this.staticRoutes.includes(currentPath) && !this.Session.userId) {
      return null;
    }

    const footer =
      this.staticRoutes.includes(currentPath) && !this.noFooterRoutes.includes(currentPath) ? (
        <footer>
          <div className="credentials-links">
            <a href="https://narrativ.com/">About</a>
            <a href="/#/privacy-policy">Privacy</a>
          </div>
        </footer>
      ) : null;
    const navHeader = !this.hideNav(currentPath) ? (
      <NavHeader
        currentUser={this.state.currentUser}
        isMobile={this.state.isMobile}
        userRole={this.userRole()}
        staticRoutes={this.staticRoutes}
        toggleNavSidebar={this.toggleNavSidebar}
        navSidebarCollapsed={this.state.navSidebarCollapsed}
        update={this.update.bind(this)}
      ></NavHeader>
    ) : null;
    const navSidebar = !this.hideNav(currentPath) ? (
      <NavSidebar
        currentUser={this.state.currentUser}
        location={this.props.location}
        history={this.props.history}
        userRole={this.userRole()}
        isAdmin={this.state.currentUser.isAdmin}
        isMobile={this.state.isMobile}
        // Path info
        restrictedPaths={this.restrictedPaths}
        exposedPaths={this.exposedPaths}
        productMatchPaths={this.productMatchPaths}
        staticRoutes={this.staticRoutes}
        wooshSpaRoutes={this.wooshSpaRoutes}
        // Booleans for display
        toggleNavSidebar={this.toggleNavSidebar}
        navSidebarCollapsed={this.state.navSidebarCollapsed}
        narrowScreen={this.narrowScreen.bind(this)}
        isAdminView={this.state.isAdminView}
        toggleAdminView={() => this.setState({ isAdminView: !this.state.isAdminView })}
      ></NavSidebar>
    ) : null;

    return (
      <div className={this.getContainerClass()}>
        {navHeader}
        {navSidebar}

        <div
          className={getClass(
            'page-container',
            `${this.moveContentRight() ? 'move-right' : 'move-left'}`,
          )}
        >
          <Routes
            isAdminView={this.state.isAdminView}
            isMobile={this.state.isMobile}
            orgType={this.Session.userRole}
          />
        </div>

        <div className="tooltip"></div>
        <Snackbar />
        {footer}
      </div>
    );
  }
}

export default withRouter(Main);
